import React, { useState, useEffect } from "react";
import LiveTrackingMap from "./LiveTrackingMap";
import VehicleList from "./VehicleList";
import tripService from "@/services/tripService";
import { FullScreenLoader } from "@/components/ui/loader";
import { useAuth } from "@/contexts/AuthContext";

const LiveTrackingPage = () => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const { userRole, userProfile } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTrips, setTotalTrips] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    const fetchActiveTrips = async () => {
      try {
        setLoading(true);
        const effectiveRole = userRole || userProfile?.role;
        const skip = (currentPage - 1) * pageSize;
        const limit = pageSize;
        
        if (effectiveRole === "transporter") {
          const transporterId = userProfile?.id;
          if (!transporterId) {
            throw new Error("Transporter ID not found in user profile");
          }
          
          let tripsList = [];
          let totalCount = 0;
          
          if (debouncedSearchTerm) {
            const [resByTrip, resByVehicle] = await Promise.all([
              tripService.getActiveTrips(transporterId, debouncedSearchTerm, "", skip, limit),
              tripService.getActiveTrips(transporterId, "", debouncedSearchTerm, skip, limit)
            ]);
            
            const listByTrip = resByTrip?.data || [];
            const listByVehicle = resByVehicle?.data || [];
            
            const mergedMap = new Map();
            listByTrip.forEach((trip) => mergedMap.set(trip.id, trip));
            listByVehicle.forEach((trip) => mergedMap.set(trip.id, trip));
            
            tripsList = Array.from(mergedMap.values());
            totalCount = (resByTrip?.total || 0) + (resByVehicle?.total || 0);
          } else {
            const data = await tripService.getActiveTrips(transporterId, "", "", skip, limit);
            tripsList = data?.data || [];
            totalCount = data?.total || 0;
          }
          
          setTrips(tripsList);
          setTotalTrips(totalCount);
        } else {
          let tripsList = [];
          let totalCount = 0;
          
          if (debouncedSearchTerm) {
            const [resByTrip, resByVehicle] = await Promise.all([
              tripService.getActiveTrips(null, debouncedSearchTerm, "", skip, limit),
              tripService.getActiveTrips(null, "", debouncedSearchTerm, skip, limit)
            ]);
            
            const listByTrip = resByTrip?.data || [];
            const listByVehicle = resByVehicle?.data || [];
            
            const mergedMap = new Map();
            listByTrip.forEach((trip) => mergedMap.set(trip.id, trip));
            listByVehicle.forEach((trip) => mergedMap.set(trip.id, trip));
            
            tripsList = Array.from(mergedMap.values());
            totalCount = (resByTrip?.total || 0) + (resByVehicle?.total || 0);
          } else {
            const data = await tripService.getActiveTrips(null, "", "", skip, limit);
            tripsList = data?.data || [];
            totalCount = data?.total || 0;
          }
          
          setTrips(tripsList);
          setTotalTrips(totalCount);
        }
        setError(null);
      } catch (err) {
        console.error("Error fetching active trips:", err);
        setError("Failed to load active trips. Please try again later.");
      } finally {
        setLoading(false);
        setIsInitialLoad(false);
      }
    };

    if (userProfile) {
      fetchActiveTrips();
    }
  }, [userRole, userProfile, debouncedSearchTerm, currentPage]);

  const vehicles = trips.map((trip) => ({
    id: trip.id,
    name: trip.vehicle?.vehicle_number || "Unknown Vehicle",
    status: trip.status,
    location:
      trip.latest_latitude &&
      trip.latest_longitude &&
      trip.latest_latitude !== -90.0 &&
      trip.latest_longitude !== -180.0
        ? `${trip.latest_latitude},${trip.latest_longitude}`
        : trip.start_latitude &&
          trip.start_longitude &&
          trip.start_latitude !== -90.0 &&
          trip.start_longitude !== -180.0
        ? `${trip.start_latitude},${trip.start_longitude}`
        : "28.6139,77.2090",
    speed: "0 km/h",
    trip: trip,
  }));

  if (isInitialLoad) {
    return <FullScreenLoader isLoading={isInitialLoad} />;
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-destructive text-center">
          <p className="font-medium">{error}</p>
          <button
            className="mt-2 px-4 py-2 bg-primary text-white rounded-md"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-6rem)] w-full overflow-hidden gap-4">
      <div>
        <h1 className="text-lg font-semibold tracking-tight text-foreground">Live Tracking</h1>
        <p className="text-xs text-muted-foreground">Real-time geographical tracking and status monitoring of active fleet.</p>
      </div>

      <div className="flex flex-1 w-full overflow-hidden rounded-2xl border border-border bg-card">
      <div className="w-full md:w-[420px] md:h-full flex flex-col border-r border-border bg-card shrink-0">
        <VehicleList
          vehicles={vehicles}
          selectedVehicle={selectedVehicle}
          onSelectVehicle={setSelectedVehicle}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          loading={loading}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          totalItems={totalTrips}
          pageSize={pageSize}
        />
      </div>

      <div className="flex-1 h-full hidden md:block relative">
        {loading && vehicles.length === 0 ? (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/5 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              <p className="text-sm font-medium text-muted-foreground">
                Syncing Live Data...
              </p>
            </div>
          </div>
        ) : null}
        <LiveTrackingMap
          vehicles={vehicles}
          selectedVehicle={selectedVehicle}
          onSelectVehicle={setSelectedVehicle}
        />
      </div>
    </div>
    </div>
  );
};

export default LiveTrackingPage;